import React, { useState } from "react";
import { Modal, Button, Card, Spin, notification } from "antd";
import { LoadingOutlined, EnvironmentOutlined } from "@ant-design/icons";
import {
  _accept_restaurant_order,
  _reject_restaurant_order,
} from "../../common/axios_services";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../common/utils/notification";

const OrderAcceptanceModal = ({
  isVisible,
  onClose,
  order,
  onOrderUpdate,
  restaurantId,
}) => {
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const handleAcceptDelivery = async () => {
    setActionLoading(true);
    try {
      const response = await _accept_restaurant_order(order.id, true);
      showSuccessNotification("Order accepted for delivery!");
      onOrderUpdate && onOrderUpdate(order.id, "ACCEPTED");
      onClose();
    } catch (err) {
      showErrorNotification(
        err.response?.data?.message || "Failed to accept order",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectDelivery = async () => {
    setActionLoading(true);
    try {
      const response = await _reject_restaurant_order(order.id);
      showSuccessNotification(
        "Order rejected. Searching for available bikers...",
      );
      onOrderUpdate && onOrderUpdate(order.id, "SEARCHING_BIKER");
      onClose();
    } catch (err) {
      showErrorNotification(
        err.response?.data?.message || "Failed to reject delivery",
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (!order) return null;

  return (
    <Modal
      title="Accept Order for Delivery?"
      open={isVisible}
      onCancel={onClose}
      footer={null}
      centered
      width={500}
      destroyOnClose
    >
      <Spin spinning={loading} indicator={antIcon}>
        <div className="p-4">
          {/* Order Details */}
          <Card className="mb-4 border-2">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Order #{order.id}
                </h3>
                <p className="text-sm text-gray-600">
                  {order.items?.length || 0} items • ₦{order.total || 0}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Customer</p>
                <p className="font-medium">
                  {order.user?.firstName} {order.user?.lastName}
                </p>
              </div>
            </div>
          </Card>

          {/* Delivery Address */}
          <Card className="mb-6 border-2 bg-blue-50">
            <div className="flex items-start gap-3">
              <EnvironmentOutlined className="text-blue-500 text-xl mt-1" />
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">
                  Delivery Address
                </h4>
                <p className="text-sm text-gray-700">
                  {order.deliveryAddress?.street || "No address provided"}
                </p>
                <p className="text-sm text-gray-600">
                  {order.deliveryAddress?.city}, {order.deliveryAddress?.state}
                </p>
                <p className="text-sm text-gray-600">
                  {order.user?.phoneCode}
                  {order.deliveryAddress?.phone || order.user?.phoneNumber}
                </p>
              </div>
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              type="primary"
              size="large"
              onClick={handleAcceptDelivery}
              loading={actionLoading}
              className="flex-1 bg-green-500 hover:bg-green-600"
            >
              ✅ Deliver Order
            </Button>
            <Button
              danger
              size="large"
              onClick={handleRejectDelivery}
              loading={actionLoading}
              className="flex-1"
            >
              ❌ Find Biker
            </Button>
          </div>

          {/* Info Text */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 text-center">
              <strong>Deliver Order:</strong> You'll handle delivery and earn
              delivery fees
            </p>
            <p className="text-sm text-gray-600 text-center mt-1">
              <strong>Find Biker:</strong> System will assign available delivery
              partner
            </p>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default OrderAcceptanceModal;
